import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';

interface AboutUsItem {
  ID: number;
  Title: string;
  Description: string;   // HTML (<p>, <strong>)
  ImagePath: string;
  DisplayOrder: number;
  IsActive: boolean;
  
  
}

@Component({
  selector: 'app-aboutus',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './aboutus.html',
  styleUrl: './aboutus.css',
})
export class Aboutus implements OnInit {
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);

  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  private readonly apiBase = 'https://crm-api.redmonkwellness.com';

  items = signal<AboutUsItem[]>([]);   // all active rows
  loading = signal(true);
  error = signal(false);

  // ngOnInit(): void {
  //   this.http
  //     .post<{ success: boolean; Items: AboutUsItem[] }>(`${this.apiBase}/Team/GetActiveAboutUs`, {})
  //     .subscribe({
  //       next: (res) => {
  //         if (res.success) {
  //           this.items.set(res.Items || []);
  //         } else {
  //           this.error.set(true);
  //         }
  //         this.loading.set(false);
  //       },
  //       error: () => {
  //         this.error.set(true);
  //         this.loading.set(false);
  //       },
  //     });
  // }


  ngOnInit(): void {

      this.setSeo();

    if (!isPlatformBrowser(this.platformId)) return;   // skip the server-side run

    this.http
      .post<{ success: boolean; Items: AboutUsItem[] }>(`${this.apiBase}/Team/GetActiveAboutUs`, {})
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.items.set(res.Items || []);
          } else {
            this.error.set(true);
          }
          this.loading.set(false);
        },
        error: (err) => {
          console.error('About Us load failed:', err);   // shows the real reason
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }




  imgUrl(path: string): string {
    if (!path) return '';
    return /^(https?:|data:)/i.test(path) ? path : this.apiBase + path;
  }



private setSeo(): void {
  const pageTitle =
    'About Red Monk Wellness | Aesthetic & Wellness Clinic';

  const description =
    'Learn about Red Monk Wellness, offering skin, hair, aesthetic and wellness treatments through its clinics in Mumbai and Kolkata.';

  const canonicalUrl =
    'https://www.redmonkwellness.com/aboutus';

  const ogImage =
    'https://www.redmonkwellness.com/assets/images/banner/banner1.jpeg';

  // TITLE
  this.title.setTitle(pageTitle);

  // BASIC META
  this.meta.updateTag({
    name: 'description',
    content: description
  });

  this.meta.updateTag({
    name: 'robots',
    content:
      'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  });

  // OPEN GRAPH
  this.meta.updateTag({
    property: 'og:type',
    content: 'website'
  });

  this.meta.updateTag({
    property: 'og:locale',
    content: 'en_IN'
  });

  this.meta.updateTag({
    property: 'og:site_name',
    content: 'Red Monk Wellness'
  });

  this.meta.updateTag({
    property: 'og:title',
    content: pageTitle
  });

  this.meta.updateTag({
    property: 'og:description',
    content: description
  });

  this.meta.updateTag({
    property: 'og:url',
    content: canonicalUrl
  });

  this.meta.updateTag({
    property: 'og:image',
    content: ogImage
  });

  this.meta.updateTag({
    property: 'og:image:alt',
    content: 'Red Monk Wellness'
  });

  this.meta.updateTag({
    property: 'og:image:type',
    content: 'image/jpeg'
  });

  // TWITTER / X
  this.meta.updateTag({
    name: 'twitter:card',
    content: 'summary_large_image'
  });

  this.meta.updateTag({
    name: 'twitter:title',
    content: pageTitle
  });

  this.meta.updateTag({
    name: 'twitter:description',
    content: description
  });

  this.meta.updateTag({
    name: 'twitter:image',
    content: ogImage
  });

  this.meta.updateTag({
    name: 'twitter:image:alt',
    content: 'Red Monk Wellness'
  });

  // CANONICAL URL
  let canonical = this.document.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;

  if (!canonical) {
    canonical = this.document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    this.document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', canonicalUrl);

  // SITEMAP
  let sitemap = this.document.querySelector(
    'link[rel="sitemap"]'
  ) as HTMLLinkElement | null;

  if (!sitemap) {
    sitemap = this.document.createElement('link');
    sitemap.setAttribute('rel', 'sitemap');
    sitemap.setAttribute('type', 'application/xml');
    sitemap.setAttribute('title', 'Sitemap');
    this.document.head.appendChild(sitemap);
  }

  sitemap.setAttribute(
    'href',
    'https://www.redmonkwellness.com/sitemap.xml'
  );

  // ORGANIZATION SCHEMA
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://www.redmonkwellness.com/#organization',
    name: 'Red Monk Wellness',
    url: 'https://www.redmonkwellness.com/',
    logo: {
      '@type': 'ImageObject',
      url: 'https://www.redmonkwellness.com/assets/images/Redmonk-Wellness-Logo-w.png'
    },
    description:
      'Red Monk Wellness offers skin, hair, aesthetic and wellness treatments through its clinics in Mumbai and Kolkata.',
    sameAs: [
      'https://www.linkedin.com/company/red-monk-wellness',
      'https://www.facebook.com/redmonkwellness'
    ]
  };

  // WEBPAGE SCHEMA
  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: pageTitle,
    headline: 'About Red Monk Wellness',
    description,
    isPartOf: {
      '@type': 'WebSite',
      '@id': 'https://www.redmonkwellness.com/#website',
      name: 'Red Monk Wellness',
      url: 'https://www.redmonkwellness.com/'
    },
    about: {
      '@id': 'https://www.redmonkwellness.com/#organization'
    },
    breadcrumb: {
      '@id': `${canonicalUrl}#breadcrumb`
    },
    inLanguage: 'en-IN'
  };

  // BREADCRUMB SCHEMA
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${canonicalUrl}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.redmonkwellness.com/home'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'About Us',
        item: canonicalUrl
      }
    ]
  };

  // ADD JSON-LD SCHEMAS
  const schemas = [
    {
      id: 'organization-schema',
      data: organizationSchema
    },
    {
      id: 'webpage-schema',
      data: webpageSchema
    },
    {
      id: 'breadcrumb-schema',
      data: breadcrumbSchema
    }
  ];

  schemas.forEach(({ id, data }) => {
    let script = this.document.getElementById(
      id
    ) as HTMLScriptElement | null;

    if (!script) {
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      this.document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(data);
  });
}


}